import { useState } from "react";

import { Sparkles, User } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "@/components/ui/sonner";
import { Textarea } from "@/components/ui/textarea";
import { brainDumpSchema, validateFormData } from "@/lib/validation";

interface BrainDumpFormProps {
  brainDump: string;
  onBrainDumpChange: (value: string) => void;
  onSubmit: (mode: 'manual' | 'automated') => void;
  isSubmitting: boolean;
}

const BrainDumpForm = ({ brainDump, onBrainDumpChange, onSubmit, isSubmitting }: BrainDumpFormProps) => {
  const [refinementMode, setRefinementMode] = useState<'manual' | 'automated'>('manual');

  const handleSubmit = () => {
    // Validate input using Zod schema
    const validation = validateFormData(brainDumpSchema, { brainDump });
    
    if (!validation.success) {
      const firstError = Object.values((validation as { success: false; errors: Record<string, string> }).errors)[0];
      toast.error(firstError);
      return;
    }
    onSubmit(refinementMode);
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-semibold">Let's Figure It Out Together</h1>
        <p className="text-muted-foreground">
          No problem. Let's start with what's on your mind
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Your Brain Dump</CardTitle>
          <CardDescription>
            Don't worry about making it perfect—a messy brain dump is a great place to start
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            value={brainDump}
            onChange={(e) => onBrainDumpChange(e.target.value)}
            placeholder="Tell me what's on your mind. Share your thoughts, worries, challenges, ideas—whatever comes up..."
            rows={12}
            className="resize-none"
          />

          <div className="space-y-3 pt-4">
            <Label>Generation Mode</Label>
            <RadioGroup value={refinementMode} onValueChange={(v: any) => setRefinementMode(v)}>
              <div className="flex items-start space-x-2 rounded-lg border p-3 has-[:checked]:border-primary">
                <RadioGroupItem value="manual" id="manual-braindump" />
                <div className="flex-1">
                  <Label htmlFor="manual-braindump" className="font-medium cursor-pointer flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Manual Curation
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    AI suggests expert personas, and you generate the details for each one. More control, more involvement.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-2 rounded-lg border p-3 has-[:checked]:border-primary">
                <RadioGroupItem value="automated" id="automated-braindump" />
                <div className="flex-1">
                  <Label htmlFor="automated-braindump" className="font-medium cursor-pointer flex items-center gap-2">
                    <Sparkles className="h-4 w-4" />
                    Fully Automated
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    AI generates the board name, description, and all persona details in one go. Faster, less intervention.
                  </p>
                </div>
              </div>
            </RadioGroup>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full"
          >
            {isSubmitting ? "Generating..." : "Next: Generate Board"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default BrainDumpForm;
